
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, forwardRef, inject, Injector, input, model, numberAttribute, OnInit, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DECORATOR_HOST, DecoratorHost } from '../../../directives/decorator-host';
import { ValueChangedEvent } from 'devextreme/ui/date_box';
import { DxDateBoxModule } from 'devextreme-angular';
import { DatetimeType } from 'src/app/controls/models/datetime-type.model';
import { DateTimeService2 } from 'src/app/shared/datetime/datetime.service2';

@Component({
  selector: 'app-datetimebox',
  templateUrl: './datetimebox.component.html',
  styleUrl: './datetimebox.component.scss',
  imports: [DxDateBoxModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimeboxComponent),
      multi: true
    },
    {
      provide: DECORATOR_HOST,
      useExisting: forwardRef(() => DatetimeboxComponent)
    }
  ],
  host: {
    class: 'link-component'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DatetimeboxComponent implements OnInit, ControlValueAccessor, DecoratorHost {
  private injector = inject(Injector);
  readonly dateTimeService = inject(DateTimeService2);

  ngControl: NgControl | null = null;

  // Accepts boolean OR string ("true"/"false"/"" counts as true)
  readonly readonly = input<boolean, unknown>(false, { transform: booleanAttribute });

  // Accepts Date, ISO string, Unix timestamp (number), null
  readonly minDate = input<Date | null, Date | string | number | null | undefined>(null, { transform: dateAttribute });
  readonly maxDate = input<Date | null, Date | string | number | null | undefined>(null, { transform: dateAttribute });

  // Accepts number OR string ("15") – falls back to 15 on invalid input
  readonly timeInterval = input<number, unknown>(15, { transform: (value: unknown) => numberAttribute(value, 15) });

  type = model<DatetimeType>(DatetimeType.DateTime);
  readonly value = model<Date | null>(null);
  readonly disabled = model<boolean>(false);

  private readonly inputId = signal<string | null>(null);
  protected readonly inputAttr = computed<Record<string, string>>(() => this.inputId() ? { id: this.inputId() } : {});

  protected readonly displayFormat = computed(() => {
    switch (this.type()) {
      case DatetimeType.Date:
        return this.dateTimeService.getDateFormat();
      case DatetimeType.Time:
        return this.dateTimeService.getTimeFormat();
      case DatetimeType.DateTime:
      default:
        return this.dateTimeService.getDateTimeFormat();
    }
  });

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    }
    catch {
      // No NgControl
    }
  }

  get control(): FormControl | null {
    return (this.ngControl?.control as FormControl) ?? null;
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.onChange(e.value);
      this.value.set(e.value as Date);
    }
  }

  /*
   * ControlValueAccessor interface methods
   */
  writeValue(obj: Date): void {
    this.value.set(obj);
  }

  private onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }

  /*
   * DecoratorHost interface methods
   */
  setInputId(inputId: string | null): void {
    this.inputId.set(inputId);
  }

  getInputId(): string | null {
    return this.inputId();
  }
}

/**
 * Coerces an attribute binding value to a Date or null.
 * Follows the same pattern as Angular's built-in booleanAttribute / numberAttribute.
 *
 * Accepts:
 *  - Date  → returned as-is (Invalid Date → null)
 *  - number → treated as Unix timestamp (ms) – must be finite
 *  - string → parsed via Date constructor – empty/whitespace-only → null
 *  - null / undefined → null
 *
 * @example
 * readonly minDate = input<Date | null, Date | string | number | null | undefined>(
 *   null, { transform: dateAttribute }
 * );
 */
export function dateAttribute(
  value: Date | string | number | null | undefined
): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? new Date(value) : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}